import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, CalendarClock, CalendarDays, UserX, RefreshCw } from 'lucide-react';
import { statusLabel, ETAPA_SHORT } from './crmLabels';

function Section({ title, icon, items, onOpenClient, accent, empty }) {
  const Icon = icon;
  return (
    <Card data-testid={`crm-azi-${title}`}>
      <CardContent className="p-4">
        <div className={`flex items-center gap-2 mb-3 ${accent}`}>
          <Icon className="h-4 w-4" />
          <h3 className="font-semibold text-sm">{title}</h3>
          <Badge variant="secondary" className="ml-auto">{items.length}</Badge>
        </div>
        <div className="space-y-2">
          {items.length === 0 && <p className="text-xs text-gray-400">{empty}</p>}
          {items.map((it) => (
            <button
              key={it.solicitareId}
              className="w-full text-left border rounded-lg p-2 hover:bg-gray-50"
              onClick={() => onOpenClient(it.clientId)}
              data-testid={`crm-azi-item-${it.clientId}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{it.nume}</span>
                <Badge variant="outline" className="text-[10px]">{ETAPA_SHORT[it.etapa]}</Badge>
              </div>
              <div className="text-xs text-gray-500">{it.telefon} · {statusLabel(it.status)}</div>
              {it.dobandaFixaExpira && (
                <div className={`text-xs mt-1 ${it.luniPanaLaExpirare != null && it.luniPanaLaExpirare <= 3 ? 'text-orange-600 font-medium' : 'text-purple-600'}`}>
                  Dobândă fixă expiră: {it.dobandaFixaExpira}
                  {it.luniPanaLaExpirare != null ? ` (~${Math.max(it.luniPanaLaExpirare, 0)} luni)` : ''}
                  {it.banca ? ` · ${it.banca}` : ''}
                </div>
              )}
              {it.urmatorulPas?.text && (
                <div className="text-xs text-gray-600 mt-1">
                  {it.urmatorulPas.text} {it.urmatorulPas.data ? `(${it.urmatorulPas.data})` : ''}
                </div>
              )}
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AziView({ api, onOpenClient }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    for (let i = 0; i < 4; i++) {
      try {
        const d = await api.get('/today');
        if (d) { setData(d); return; }
      } catch (e) { /* retry */ }
      await new Promise((r) => setTimeout(r, 800));
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-gray-500">Se încarcă…</p>;

  return (
    <div data-testid="crm-azi" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <Section title="Taskuri restante" icon={AlertTriangle} items={data.taskuriRestante}
        onOpenClient={onOpenClient} accent="text-red-600" empty="Nimic restant 🎉" />
      <Section title="Taskuri azi" icon={CalendarDays} items={data.taskuriAzi}
        onOpenClient={onOpenClient} accent="text-blue-600" empty="Nimic pentru azi" />
      <Section title="Clienți fără următorul pas" icon={UserX} items={data.clientiFaraPas}
        onOpenClient={onOpenClient} accent="text-amber-600" empty="Toți au pas setat" />
      <Section title="Preaprobări care expiră" icon={CalendarClock} items={data.preaprobariExpira}
        onOpenClient={onOpenClient} accent="text-orange-600" empty="Nicio preaprobare care expiră curând" />
      <Section title="Retenția lunii" icon={RefreshCw} items={data.retentie}
        onOpenClient={onOpenClient} accent="text-purple-600" empty="Niciun client în retenție" />
    </div>
  );
}
